import { ColDef, ValueFormatterParams, ValueGetterParams } from "ag-grid-community";

import * as CubiqTS from "shared/cubiq-model-multi-asset";
import { BfxLabelHelper } from "../../bfx-common/helpers/bfx-label-helper";
import { COLUMN_PRIVATIZATION } from "shared/cubiq-services";

export const BM_MULTI_ALLOCATION_COL_INTENT = "allocationIntent";
export const BM_MULTI_ALLOCATION_COL_FIXED_CCY = "fixedCcy"; // in model?
export const BM_MULTI_ALLOCATION_COL_AMOUNT = "allocationQty";
export const BM_MULTI_ALLOCATION_COL_ACCOUNT = "accountCode";
export const BM_MULTI_ALLOCATION_COL_TENOR = "settleTenor";
export const BM_MULTI_ALLOCATION_COL_VALUE_DATE = "settleDate";
export const BM_MULTI_ALLOCATION_COL_PRODUCT = "product";
export const BM_MULTI_ALLOCATION_COL_SELECTION = "selection";

// fields from FixingOrderRequestData
const BM_COL_CLIENT_INTENT = "clientIntent";
const UNMATCHED_VALUE_DATE = Symbol("UNMATCHED_TENOR_VALUE_DATE");

const AMT_VALUE_FORMATTER = function (this: void, value: string) {
  if (value == null) {
    return "";
  }

  let num = `${value}`.split(".");

  num[0] = num[0].replace(/(\d)(?=(\d{3})+(?!\d))/g, "$1,");

  return num.join(".");
};

const FIXING_ORDER_REQUEST_MATCH_ALL_ALLOCS = (
  params: ValueFormatterParams | ValueGetterParams,
  fixingRequestField,
  allocationField,
  getValueFn: (data: any, field) => any = null,
  valueIsEqual: (a, b) => boolean = null,
) => {
  if (params.data instanceof CubiqTS.FixingOrderRequestDataImpl) {
    let value =
      getValueFn != null
        ? getValueFn(params.data, fixingRequestField)
        : params.data[fixingRequestField];
    let match = true;

    params.api.forEachNode((node, _) => {
      if (!match) return;
      if (node.data === params.data) return;
      let nodeValue = node.data[allocationField];
      if (typeof nodeValue === "string") {
        if (value !== nodeValue) match = false;
      } else if (getValueFn != null && valueIsEqual != null) {
        if (!valueIsEqual(value, getValueFn(node.data, allocationField))) match = false;
      } else if (getValueFn != null) {
        if (value !== getValueFn(node.data, allocationField)) match = false;
      } else {
        match = false;
      }
    });
    return match;
  }
  return true;
};

export const BM_MULTI_ALLOCATION_COLS: Array<ColDef> = [
  {
    field: BM_MULTI_ALLOCATION_COL_PRODUCT,
    headerName: "Product",
    width: 55,
    editable: false,
    hide: true,
    valueFormatter: (params) => {
      return CubiqTS.Product[params.data[BM_MULTI_ALLOCATION_COL_PRODUCT]];
    },
  },
  {
    field: BM_MULTI_ALLOCATION_COL_SELECTION,
    headerName: "",
    width: 55,
    editable: false,
    headerClass: "ag-right-aligned-header",
    checkboxSelection: (params) => {
      if (params.data instanceof CubiqTS.FixingOrderRequestDataImpl) return false;

      if (params.context.isBulked()) return false;

      return true;
    },
  },
  {
    field: BM_MULTI_ALLOCATION_COL_INTENT,
    headerName: "Intent",
    width: 71,
    editable: true,
    headerClass: "ag-right-aligned-header",
    cellClass: "editable",
    cellEditor: BM_MULTI_ALLOCATION_COL_INTENT,
    valueFormatter: (params) => {
      if (params.data instanceof CubiqTS.FixingOrderRequestDataImpl) {
        return FIXING_ORDER_REQUEST_MATCH_ALL_ALLOCS(
          params,
          BM_COL_CLIENT_INTENT,
          BM_MULTI_ALLOCATION_COL_INTENT,
          (data, col) => data[col],
        )
          ? params.data[BM_COL_CLIENT_INTENT] === CubiqTS.OrderIntent.BUY
            ? "BUY"
            : "SELL"
          : "";
      }

      return params.data[BM_MULTI_ALLOCATION_COL_INTENT] === CubiqTS.OrderIntent.BUY
        ? "BUY"
        : "SELL";
    },
  },
  {
    field: BM_MULTI_ALLOCATION_COL_FIXED_CCY,
    headerName: "Fixed",
    width: 40,
    editable: false,
    cellEditor: BM_MULTI_ALLOCATION_COL_FIXED_CCY,
  },
  {
    field: BM_MULTI_ALLOCATION_COL_AMOUNT,
    headerName: "Amount",
    width: 120,
    editable: true,
    cellClass: "editable",
    cellEditor: BM_MULTI_ALLOCATION_COL_AMOUNT,
    valueFormatter: (params) => {
      if (
        params.data instanceof CubiqTS.FixingOrderRequestDataImpl &&
        CubiqTS.Product.SWP === params.data.product
      ) {
        let fixedAmount = params.data["fixedAmount"];
        let swapFixedAmount = params.data["swapFixedAmount"];
        return `${AMT_VALUE_FORMATTER(fixedAmount)} / ${AMT_VALUE_FORMATTER(swapFixedAmount)}`;
      } else if (params.data instanceof CubiqTS.FixingOrderRequestDataImpl) {
        let fixedAmount = params.data["fixedAmount"];
        return AMT_VALUE_FORMATTER(fixedAmount);
      }

      if (typeof params.data[BM_MULTI_ALLOCATION_COL_AMOUNT] === "string") {
        return AMT_VALUE_FORMATTER(params.data[BM_MULTI_ALLOCATION_COL_AMOUNT]);
      }

      return AMT_VALUE_FORMATTER(`${params.data[BM_MULTI_ALLOCATION_COL_AMOUNT]}`);
    },
  },
  {
    field: BM_MULTI_ALLOCATION_COL_ACCOUNT,
    headerName: "Account",
    width: 80,
    editable: true,
    cellClass: "editable",
    cellEditor: BM_MULTI_ALLOCATION_COL_ACCOUNT,
    valueFormatter: (params) => {
      if (
        FIXING_ORDER_REQUEST_MATCH_ALL_ALLOCS(
          params,
          BM_MULTI_ALLOCATION_COL_ACCOUNT,
          BM_MULTI_ALLOCATION_COL_ACCOUNT,
        )
      ) {
        let acct: string = params.data[BM_MULTI_ALLOCATION_COL_ACCOUNT];
        let privatised: boolean = null != acct && 0 == acct.indexOf(COLUMN_PRIVATIZATION);
        if (privatised) {
          return "";
        }
        return acct;
      } else {
        return "";
      }
    },
  },
  {
    field: BM_MULTI_ALLOCATION_COL_TENOR,
    headerName: "Tenor",
    width: 60,
    editable: true,
    cellClass: "editable",
    valueFormatter: (params: ValueFormatterParams) => {
      const col =
        params.data instanceof CubiqTS.FixingOrderRequestDataImpl
          ? "tenor"
          : BM_MULTI_ALLOCATION_COL_TENOR;

      if (
        !FIXING_ORDER_REQUEST_MATCH_ALL_ALLOCS(
          params,
          col,
          BM_MULTI_ALLOCATION_COL_TENOR,
          (data, col) => data[col],
        )
      ) {
        return "";
      }

      return params.data[col] != null ? BfxLabelHelper.getTenorLabel(params.data[col]) : "-";
    },
    cellEditor: BM_MULTI_ALLOCATION_COL_TENOR,
  },
  {
    field: BM_MULTI_ALLOCATION_COL_VALUE_DATE,
    headerName: "Value Date",
    width: 90,
    editable: true,
    cellEditor: BM_MULTI_ALLOCATION_COL_VALUE_DATE,
    cellClass: "editable",
    valueGetter: (params: ValueGetterParams) => {
      const col =
        params.data instanceof CubiqTS.FixingOrderRequestDataImpl
          ? "valueDate"
          : BM_MULTI_ALLOCATION_COL_VALUE_DATE;

      if (
        params.data instanceof CubiqTS.FixingOrderRequestDataImpl &&
        FIXING_ORDER_REQUEST_MATCH_ALL_ALLOCS(
          params,
          "valueDate",
          BM_MULTI_ALLOCATION_COL_VALUE_DATE,
          (data, col) => data[col],
          (a: CubiqTS.StandardDateImpl, b: CubiqTS.StandardDateImpl) => {
            let aDate = new Date(0);
            let bDate = new Date(0);
            if (a != null) {
              aDate.setFullYear(a.year);
              aDate.setMonth(a.month - 1);
              aDate.setDate(a.day);
            }
            if (b != null) {
              bDate.setFullYear(b.year);
              bDate.setMonth(b.month - 1);
              bDate.setDate(b.day);
            }
            return aDate.getTime() == bDate.getTime();
          },
        ) == false
      ) {
        return UNMATCHED_VALUE_DATE;
      }

      if (
        params.data != null &&
        params.data[col] != null &&
        params.data[col] instanceof CubiqTS.StandardDateImpl
      ) {
        let value = params.data[col];
        let date = new Date(0);
        date.setFullYear(value.year);
        date.setMonth(value.month - 1);
        date.setDate(value.day);
        return date;
      } else if (
        params.data != null &&
        params.data[col] != null &&
        params.data[col] instanceof Date
      ) {
        return params.data[col];
      }
      return null;
    },
    valueSetter: (params) => {
      if (params.newValue != null && params.newValue instanceof Date) {
        params.data[BM_MULTI_ALLOCATION_COL_TENOR] = null;
        params.data[BM_MULTI_ALLOCATION_COL_VALUE_DATE] = params.newValue;
        return true;
      } else {
        return false;
      }
    },
    valueFormatter: (params: ValueFormatterParams) => {
      if (params.value === UNMATCHED_VALUE_DATE) {
        return "";
      } else if (params.value != null) {
        let mDate;
        if (params.value instanceof Date) {
          mDate = CubiqTS.moment(params.value);
        } else if (params.value instanceof CubiqTS.StandardDateImpl) {
          let date = new Date(0);
          date.setFullYear(params.value.year);
          date.setMonth(params.value.month - 1);
          date.setDate(params.value.day);
          mDate = CubiqTS.moment(date);
        }
        return mDate.format("DD-MMM-YYYY");
      }

      return "-";
    },
  },
];
